import { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Typography,
  Stack,
} from '@mui/material';
import {
  SERVICE_TYPES,
  CONTACT_METHODS,
  SMS_CONSENT_TEXT,
  PRIVACY_POLICY_VERSION,
  TERMS_VERSION,
  serviceRequestSchema,
} from '../lib/validation';
import { submitServiceRequest } from '../lib/api';

interface FormData {
  fullName: string;
  companyName: string;
  email: string;
  phone: string;
  serviceType: string;
  preferredContactMethod: string;
  message: string;
  smsConsent: boolean;
}

const initialFormData: FormData = {
  fullName: '',
  companyName: '',
  email: '',
  phone: '',
  serviceType: '',
  preferredContactMethod: '',
  message: '',
  smsConsent: false,
};

interface Props {
  turnstileToken: string;
}

function RequestServicesForm({ turnstileToken }: Props) {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({ ...prev, smsConsent: e.target.checked }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setSubmitStatus(null);

    const dataToValidate = {
      ...formData,
      serviceType: formData.serviceType as typeof SERVICE_TYPES[number],
      preferredContactMethod: formData.preferredContactMethod
        ? (formData.preferredContactMethod as typeof CONTACT_METHODS[number])
        : undefined,
      turnstileToken,
    };

    const result = serviceRequestSchema.safeParse(dataToValidate);

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        const field = err.path[0] as string;
        fieldErrors[field] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await submitServiceRequest({
        ...result.data,
        smsConsentText: formData.smsConsent ? SMS_CONSENT_TEXT : undefined,
        privacyPolicyVersion: PRIVACY_POLICY_VERSION,
        termsVersion: TERMS_VERSION,
        sourceUrl: '/request-services',
      } as Parameters<typeof submitServiceRequest>[0]);

      if (response.success) {
        setSubmitStatus({
          type: 'success',
          message:
            'Thank you for your request! We will review your submission and get back to you shortly.',
        });
        setFormData(initialFormData);
      } else {
        setSubmitStatus({
          type: 'error',
          message: response.message,
        });
      }
    } catch {
      setSubmitStatus({
        type: 'error',
        message: 'An unexpected error occurred. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus?.type === 'success') {
    return (
      <Alert severity="success" sx={{ mt: 2 }}>
        {submitStatus.message}
      </Alert>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Stack spacing={3}>
        {submitStatus?.type === 'error' && (
          <Alert severity="error">{submitStatus.message}</Alert>
        )}

        <TextField
          required
          fullWidth
          label="Full Name"
          name="fullName"
          value={formData.fullName}
          onChange={handleChange}
          error={!!errors.fullName}
          helperText={errors.fullName}
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label="Company Name (Optional)"
          name="companyName"
          value={formData.companyName}
          onChange={handleChange}
          disabled={isSubmitting}
        />

        <TextField
          required
          fullWidth
          label="Email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          error={!!errors.email}
          helperText={errors.email}
          disabled={isSubmitting}
        />

        <TextField
          fullWidth
          label="Phone (Optional)"
          name="phone"
          type="tel"
          value={formData.phone}
          onChange={handleChange}
          disabled={isSubmitting}
        />

        <TextField
          required
          fullWidth
          select
          label="Service Type"
          name="serviceType"
          value={formData.serviceType}
          onChange={handleChange}
          error={!!errors.serviceType}
          helperText={errors.serviceType}
          disabled={isSubmitting}
        >
          {SERVICE_TYPES.map((type) => (
            <MenuItem key={type} value={type}>
              {type}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          select
          label="Preferred Contact Method (Optional)"
          name="preferredContactMethod"
          value={formData.preferredContactMethod}
          onChange={handleChange}
          disabled={isSubmitting}
        >
          <MenuItem value="">No preference</MenuItem>
          {CONTACT_METHODS.map((method) => (
            <MenuItem key={method} value={method}>
              {method}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          required
          fullWidth
          multiline
          rows={4}
          label="Message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          error={!!errors.message}
          helperText={
            errors.message || 'Please describe your project or service needs'
          }
          disabled={isSubmitting}
        />

        <Box className="consent-box">
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.smsConsent}
                onChange={handleCheckboxChange}
                disabled={isSubmitting}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary">
                {SMS_CONSENT_TEXT}
              </Typography>
            }
            sx={{ alignItems: 'flex-start', m: 0 }}
          />
        </Box>

        {errors.turnstileToken && (
          <Alert severity="error">{errors.turnstileToken}</Alert>
        )}

        <Box id="turnstile-widget-request" sx={{ minHeight: 65 }}>
          {/* Turnstile widget will be rendered here */}
        </Box>

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={isSubmitting}
          sx={{ py: 1.5 }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Submit Request'
          )}
        </Button>
      </Stack>
    </Box>
  );
}

export default RequestServicesForm;
